import React, { useEffect, useMemo, useState } from "react";

const pageWrap = {
  padding: "28px 22px 60px",
  maxWidth: "1180px",
  margin: "0 auto",
};

const panel = {
  background: "#102a3d",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: 14,
  padding: 20,
  marginBottom: 18,
};

const statGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
  gap: 14,
};

const statCard = {
  background: "#082038",
  borderRadius: 10,
  padding: "16px 18px",
};

const label = {
  color: "#9db7cc",
  fontSize: 13,
  marginBottom: 8,
};

const value = {
  color: "#fff",
  fontSize: 28,
  fontWeight: 800,
};

const fieldGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: 12,
};

const fieldBox = {
  background: "#0b2238",
  borderRadius: 10,
  padding: "13px 14px",
};

const badge = (kind) => ({
  display: "inline-flex",
  alignItems: "center",
  borderRadius: 999,
  padding: "5px 10px",
  fontWeight: 800,
  fontSize: 12,
  color: "#fff",
  background:
    kind === "connected"
      ? "#18a058"
      : kind === "pending"
      ? "#c48a17"
      : kind === "enabled"
      ? "#0ea5e9"
      : "#5b6775",
});

const goldBtn = {
  background: "#e5bf4f",
  color: "#06131f",
  border: 0,
  borderRadius: 9,
  padding: "11px 14px",
  fontWeight: 800,
  cursor: "pointer",
};

const blueBtn = {
  background: "#00c2ff",
  color: "#06131f",
  border: 0,
  borderRadius: 9,
  padding: "11px 14px",
  fontWeight: 800,
  cursor: "pointer",
};

const grayBtn = {
  background: "#334155",
  color: "#fff",
  border: 0,
  borderRadius: 9,
  padding: "11px 14px",
  fontWeight: 800,
  cursor: "pointer",
};

export default function PlatformStripeConnect() {
  const [data, setData] = useState(null);
  const [selectedTenantId, setSelectedTenantId] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const res = await fetch("/api/platform/billing/stripe-connect/summary", {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to load Stripe Connect Payments.");
      const result = await res.json();

      const rows = result.tenants || [];

      const normalized = rows.map((t) => ({
        id: t.id,
        name: t.name,
        subdomain: t.subdomain,
        tenant_type: t.tenant_type || "standard",
        billing_mode: t.billing_mode || "free",
        subscription_plan: t.subscription_plan || "basic",
        subscription_status: t.subscription_status || "active",
        stripe_connect_enabled: !!t.stripe_connect_enabled,
        stripe_connect_account_id: t.stripe_connect_account_id || null,
        stripe_connect_status: t.stripe_connect_status || null,
        stripe_connect_onboarding_complete: !!t.stripe_connect_onboarding_complete,
        stripe_connect_charges_enabled: !!t.stripe_connect_charges_enabled,
        stripe_connect_payouts_enabled: !!t.stripe_connect_payouts_enabled,
        stripe_connect_details_submitted: !!t.stripe_connect_details_submitted,
      }));

      setData(normalized);

      if (!selectedTenantId && normalized.length) {
        const dfasio = normalized.find((t) => t.subdomain === "dfasio");
        setSelectedTenantId(String((dfasio || normalized[0]).id));
      }
    } catch (e) {
      setErr(e.message || "Failed to load Stripe Connect Payments.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tenants = data || [];

  const participating = tenants.filter(
    (t) => t.stripe_connect_enabled || t.stripe_connect_account_id
  );

  const selectorRows = participating.length ? participating : tenants;

  const selectedTenant = useMemo(() => {
    return selectorRows.find((t) => String(t.id) === String(selectedTenantId)) || null;
  }, [selectorRows, selectedTenantId]);

  const connectedCount = tenants.filter(
    (t) =>
      t.stripe_connect_status === "connected" ||
      t.stripe_connect_onboarding_complete
  ).length;

  const pendingCount = tenants.filter(
    (t) =>
      t.stripe_connect_status === "onboarding_pending" ||
      (t.stripe_connect_account_id && !t.stripe_connect_onboarding_complete)
  ).length;

  const enabledCount = tenants.filter(
    (t) => t.stripe_connect_enabled || t.stripe_connect_account_id
  ).length;

  const disabledCount = Math.max(0, tenants.length - enabledCount);

  function statusKind(t) {
    if (!t) return "disabled";
    if (t.stripe_connect_onboarding_complete || t.stripe_connect_status === "connected") {
      return "connected";
    }
    if (t.stripe_connect_account_id) return "pending";
    if (t.stripe_connect_enabled) return "enabled";
    return "disabled";
  }

  function statusText(t) {
    if (!t) return "No tenant selected";
    if (t.stripe_connect_onboarding_complete || t.stripe_connect_status === "connected") {
      return "Connected";
    }
    if (t.stripe_connect_account_id) return "Onboarding Pending";
    if (t.stripe_connect_enabled) return "Enabled";
    return "Disabled";
  }

  async function postTenantAction(action) {
    if (!selectedTenant) return;

    setErr("");

    try {
      const res = await fetch(
        `/api/platform/billing/stripe-connect/tenant/${selectedTenant.id}/${action}`,
        {
          method: "POST",
          credentials: "include",
        }
      );

      const result = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(result.detail || result.error || `Failed to ${action} Stripe Connect.`);
      }

      if (action === "onboarding-link" && result.url) {
        window.location.href = result.url;
        return;
      }

      await load();
    } catch (e) {
      setErr(e.message || `Failed to ${action} Stripe Connect.`);
    }
  }

  return (
    <main style={pageWrap}>
        <div style={{ marginBottom: 22 }}>
          <h1 style={{ margin: "0 0 8px" }}>Stripe Connect Payments</h1>
          <p style={{ color: "#9db7cc", margin: 0 }}>
            Manage which tenants can accept invoice payments through JobTelesis.
          </p>
        </div>

        {err && (
          <div style={{ ...panel, borderColor: "#ef4444", color: "#fecaca" }}>
            {err}
          </div>
        )}

        <section style={panel}>
          <h2 style={{ marginTop: 0 }}>Connect Summary</h2>
          <div style={statGrid}>
            <div style={statCard}>
              <div style={label}>Participating Tenants</div>
              <div style={value}>{enabledCount}</div>
            </div>
            <div style={statCard}>
              <div style={label}>Connected Accounts</div>
              <div style={value}>{connectedCount}</div>
            </div>
            <div style={statCard}>
              <div style={label}>Pending Onboarding</div>
              <div style={value}>{pendingCount}</div>
            </div>
            <div style={statCard}>
              <div style={label}>Connect Disabled</div>
              <div style={value}>{disabledCount}</div>
            </div>
            <div style={statCard}>
              <div style={label}>Revenue Processed</div>
              <div style={value}>$0</div>
            </div>
            <div style={statCard}>
              <div style={label}>Platform Fee Revenue</div>
              <div style={value}>$0</div>
            </div>
          </div>
        </section>

        <section style={panel}>
          <h2 style={{ marginTop: 0 }}>Tenant Payment Command Center</h2>
          <p style={{ color: "#9db7cc" }}>
            Choose one participating tenant to inspect or manage their Stripe Connect payment setup.
          </p>

          <select
            value={selectedTenantId}
            onChange={(e) => setSelectedTenantId(e.target.value)}
            style={{
              width: "100%",
              maxWidth: 520,
              padding: "12px 14px",
              borderRadius: 9,
              border: "1px solid rgba(255,255,255,0.15)",
              background: "#081d31",
              color: "#fff",
              fontWeight: 700,
              marginBottom: 18,
            }}
          >
            {selectorRows.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} — {t.subdomain} — Tenant ID {t.id}
              </option>
            ))}
          </select>

          {loading ? (
            <div style={{ color: "#9db7cc" }}>Loading Stripe Connect Payments...</div>
          ) : !selectedTenant ? (
            <div style={{ color: "#9db7cc" }}>No tenants available.</div>
          ) : (
            <div
              style={{
                background: "#071c2e",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 14,
                padding: 20,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 14,
                  alignItems: "flex-start",
                  marginBottom: 16,
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <h2 style={{ margin: 0 }}>{selectedTenant.name}</h2>
                  <div style={{ color: "#9db7cc", marginTop: 5 }}>
                    {selectedTenant.subdomain} · Tenant ID {selectedTenant.id}
                  </div>
                </div>
                <span style={badge(statusKind(selectedTenant))}>
                  {statusText(selectedTenant)}
                </span>
              </div>

              <div style={fieldGrid}>
                <div style={fieldBox}>
                  <div style={label}>Invoice Payments Enabled</div>
                  <strong>{selectedTenant.stripe_connect_enabled ? "Yes" : "No"}</strong>
                </div>
                <div style={fieldBox}>
                  <div style={label}>Charges Enabled</div>
                  <strong>{selectedTenant.stripe_connect_charges_enabled ? "Yes" : "No"}</strong>
                </div>
                <div style={fieldBox}>
                  <div style={label}>Payouts Enabled</div>
                  <strong>{selectedTenant.stripe_connect_payouts_enabled ? "Yes" : "No"}</strong>
                </div>
                <div style={fieldBox}>
                  <div style={label}>Details Submitted</div>
                  <strong>{selectedTenant.stripe_connect_details_submitted ? "Yes" : "No"}</strong>
                </div>
                <div style={fieldBox}>
                  <div style={label}>Connect Account ID</div>
                  <strong>{selectedTenant.stripe_connect_account_id || "None"}</strong>
                </div>
                <div style={fieldBox}>
                  <div style={label}>Billing Mode</div>
                  <strong>{selectedTenant.billing_mode}</strong>
                </div>
              </div>

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 20 }}>
                <button style={goldBtn} onClick={() => postTenantAction("enable")}>
                  Enable Connect
                </button>
                <button style={grayBtn} onClick={() => postTenantAction("disable")}>
                  Disable Connect
                </button>
                <button style={blueBtn} onClick={() => postTenantAction("onboarding-link")}>
                  Start Stripe Onboarding
                </button>
                <button style={grayBtn} onClick={() => postTenantAction("refresh")}>
                  Refresh Status
                </button>
              </div>

              <p style={{ color: "#9db7cc", marginTop: 14, marginBottom: 0 }}>
                Use Enable Connect first, then Start Stripe Onboarding to open the tenant Stripe onboarding flow.
                Refresh Status checks Stripe and updates the tenant connection state.
              </p>
            </div>
          )}
        </section>
    </main>
  );
}
